import { deserializeFilter, Filter } from "./filter";
import {
  useDeleteSavedFilter,
  useListSavedFilters,
  useUpdateSavedFilter,
} from "../data/saved-filter";
import { useMemo } from "react";
import { LOB } from "../../../api/routes";
import { useUsers } from "../data";

type FilterAndFunctions = {
  id: string;
  filter: Filter;
  name: string;
  isDefault: boolean;
  setName: (name: string) => void;
  setDefault: (yes: boolean) => void;
  remove: () => void;
};
export const useFilters = (): FilterAndFunctions[] => {
  const users = useUsers();
  const lobsForUser: LOB[] = (users.data?.current_user.lobs ?? []) as LOB[];
  const filters = useListSavedFilters().data?.filters ?? [];
  const update = useUpdateSavedFilter();
  const remove = useDeleteSavedFilter();

  return useMemo(
    () =>
      filters
        .map(({ id, nickname, url, is_default: isDefault }) => {
          const filter = deserializeFilter(url, lobsForUser);
          return filter
            ? {
                id,
                filter,
                name: nickname,
                isDefault,
                setDefault: (yes: boolean) => {
                  update.mutate({ id, nickname, isDefault: yes });
                  if (yes) {
                    const prevDefault = filters.find((f) => f.is_default);
                    if (prevDefault && prevDefault.id !== id) {
                      update.mutate({
                        id: prevDefault.id,
                        nickname: prevDefault.nickname,
                        isDefault: false,
                      });
                    }
                  }
                },
                setName: (name: string) =>
                  update.mutate({ id, nickname: name, isDefault }),
                remove: () => remove.mutate({ id }),
              }
            : undefined;
        })
        .filter((x) => !!x),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, not yet audited
    [filters]
  );
};

export const useDefaultFilter = (): Filter | undefined => {
  const users = useUsers();
  const lobsForUser: LOB[] = (users.data?.current_user.lobs ?? []) as LOB[];
  const filters = useListSavedFilters()?.data?.filters ?? [];
  return useMemo(() => {
    if (filters) {
      const defaultFilter = filters.find((x) => x.is_default);
      if (defaultFilter) {
        const parsedFilter = deserializeFilter(defaultFilter.url, lobsForUser);
        if (parsedFilter) {
          return parsedFilter;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, not yet audited
  }, [filters]);
};
